import {
  Cell,
  Column,
  HintLabel,
  Pagination,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import { tooltipTexts } from '@utility/constants/common';
import type { CartonRow } from '../lib/rows';
import {
  CARTON_COLUMN_COUNT,
  type CartonRowContext,
  CartonRows,
} from './CartonRows';

export type CartonTableProps = {
  rows: CartonRow[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalData: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  rowContext: CartonRowContext;
};

const LOADING_ROWS = 7;

/** Each item's or component's terms, entity and carton configurations. */
export function CartonTable({
  rows,
  loading,
  page,
  pageSize,
  totalData,
  onPageChange,
  onPageSizeChange,
  rowContext,
}: CartonTableProps) {
  return (
    <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
      <Table aria-label="Carton configuration" className="sk:table-fixed">
        <TableHeader>
          <Column id="product" isRowHeader className="sk:w-[32%]">
            Product
          </Column>
          <Column id="price" className="sk:w-[10%]">
            Price per unit (USD)
          </Column>
          <Column id="lead_time" className="sk:w-[9%]">
            Lead time (days)
          </Column>
          <Column id="moq" className="sk:w-[7%]">
            <HintLabel hint={tooltipTexts.moqTableHeader}>MOQ</HintLabel>
          </Column>
          <Column id="entity" className="sk:w-[14%]">
            <HintLabel hint={tooltipTexts.entityTableHeader}>Entity</HintLabel>
          </Column>
          <Column id="carton" className="sk:w-[24%]">
            Carton configuration
          </Column>
          <Column id="actions" aria-label="Actions" className="sk:w-[4%]">
            {''}
          </Column>
        </TableHeader>
        <TableBody
          renderEmptyState={() => (
            <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
              Nothing found
            </div>
          )}
        >
          {loading
            ? Array.from({ length: LOADING_ROWS }, (_, row) => (
                <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                  <Cell colSpan={CARTON_COLUMN_COUNT}>
                    <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                  </Cell>
                </Row>
              ))
            : rows.map(row => (
                <CartonRows key={row.id} row={row} context={rowContext} />
              ))}
        </TableBody>
      </Table>
      {totalData > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalData}
          onPageChange={nextPage => {
            onPageChange(nextPage);
            window.scrollTo({ top: 0 });
          }}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </TableContainer>
  );
}
